import React, { useState, useEffect } from 'react';
import API_BASE_URL from '../../../components/apiconfig/api-config';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa';
import SearchableDropdown from '../../../components/common/SearchableDropdown';

const SalesOrder = () => {
  const [formData, setFormData] = useState({
    order_date: '',
    warehouse_id: '',
    broker_id: '',
    domestic_buyer_id: '',
    commodity_id: '',
    quantity_mt: '',
    sale_price_per_mt: '',
    total_amount: '',
    remarks: '',
    cha_cma_id: '',
    check_cma_cha: 'CMA',
    penalty_interest_pct: '',
    delivery_order_id: '',
    invoice_no: '',
    vehicle_no: '',
    issuing_authority_id: ''
  });

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [commodities, setCommodities] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [brokers, setBrokers] = useState([]);
  const [cmas, setCmas] = useState([]);
  const [chas, setChas] = useState([]);
  const [chaWarehouses, setChaWarehouses] = useState([]);
  const [deliveryOrders, setDeliveryOrders] = useState([]);
  const [domesticBuyers, setDomesticBuyers] = useState([]);
  const [issuingAuthorities, setIssuingAuthorities] = useState([]);
  const [filteredCmas, setFilteredCmas] = useState([]);
  const [filteredChas, setFilteredChas] = useState([]);
  const [selectedDeliveryOrder, setSelectedDeliveryOrder] = useState(null);
  const [selectedType, setSelectedType] = useState('CMA');
  const [quantityError, setQuantityError] = useState('');
  const [deliveryOrderQuantity, setDeliveryOrderQuantity] = useState(null);
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [invoiceError, setInvoiceError] = useState('');
  const navigate = useNavigate();

  // ← SEPARATE FUNCTION TO FETCH DELIVERY ORDERS
  const fetchDeliveryOrders = async () => {
    try {
      const deliveryOrderRes = await fetch(`${API_BASE_URL}/api/masters/dropdown-delivery-orders-for-sales`, {
        credentials: 'include'
      });
      if (deliveryOrderRes.ok) {
        const deliveryOrderData = await deliveryOrderRes.json();
        setDeliveryOrders(deliveryOrderData.data || []);
      }
    } catch (error) {
      console.error('Error fetching delivery orders:', error);
    }
  };

  // Fetch dropdown data
  useEffect(() => {
    const fetchDropdownData = async () => {
      try {
        const commodityRes = await fetch(`${API_BASE_URL}/api/masters/commodity-get`, {
          credentials: 'include'
        });
        if (commodityRes.ok) {
          const commodityData = await commodityRes.json();
          setCommodities(commodityData.data || []);
        }

        const brokerRes = await fetch(`${API_BASE_URL}/api/masters/dropdown-sale-brokers`, {
          credentials: 'include'
        });
        if (brokerRes.ok) {
          const brokerData = await brokerRes.json();
          setBrokers(brokerData.data || []);
        }

        const cmaRes = await fetch(`${API_BASE_URL}/api/masters/dropdown-cma-list`, {
          credentials: 'include'
        });
        if (cmaRes.ok) {
          const cmaData = await cmaRes.json();
          setCmas(cmaData.data || []);
        }

        const chaRes = await fetch(`${API_BASE_URL}/api/masters/dropdown-cha-list`, {
          credentials: 'include'
        });
        if (chaRes.ok) {
          const chaData = await chaRes.json();
          setChas(chaData.data || []);
        }

        // ← CALL THE SEPARATE FUNCTION
        await fetchDeliveryOrders();

        const domesticBuyerRes = await fetch(`${API_BASE_URL}/api/masters/dropdown-domestic-buyers`, {
          credentials: 'include'
        });
        if (domesticBuyerRes.ok) {
          const domesticBuyerData = await domesticBuyerRes.json();
          setDomesticBuyers(domesticBuyerData.data || []);
        }

        const issuingAuthorityRes = await fetch(`${API_BASE_URL}/api/masters/dropdown-issuing-authorities`, {
          credentials: 'include'
        });
        if (issuingAuthorityRes.ok) {
          const issuingAuthorityData = await issuingAuthorityRes.json();
          setIssuingAuthorities(issuingAuthorityData.data || []);
        }
      } catch (error) {
        console.error('Error fetching dropdown data:', error);
      }
    };
    fetchDropdownData();
  }, []);

  // Quantity validation function
  const validateQuantity = (quantity, deliveryOrderQty = deliveryOrderQuantity) => {
    if (!deliveryOrderQty || !quantity) {
      setQuantityError('');
      return;
    }

    const quantityNum = parseFloat(quantity);
    const availableQty = parseFloat(deliveryOrderQty.quantity_mt || 0) - parseFloat(deliveryOrderQty.used_quantity_mt || 0);
    
    if (availableQty <= 0) {
      setQuantityError('No quantity available for this delivery order. All quantity has been used in previous sales orders.');
    } else if (quantityNum > availableQty) {
      setQuantityError(`Quantity cannot exceed available quantity (${availableQty.toLocaleString('en-IN', {
        minimumFractionDigits: 3,
        maximumFractionDigits: 3
      })} MT)`);
    } else {
      setQuantityError('');
    }
  };

  // ← AUTO GENERATE INVOICE NO based on delivery_order_id + order_date
  const fetchGeneratedInvoiceNo = async (deliveryOrderId, orderDate) => {
    if (!deliveryOrderId || !orderDate) return; // need both to generate
    setInvoiceLoading(true);
    setInvoiceError('');
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/sales/generate-invoice-no?delivery_order_id=${deliveryOrderId}&order_date=${orderDate}`,
        { credentials: 'include' }
      );
      const data = await res.json();
      if (res.ok && data.invoice_no) {
        setFormData(prev => ({ ...prev, invoice_no: data.invoice_no }));
        setInvoiceError('');
      } else {
        setFormData(prev => ({ ...prev, invoice_no: '' }));
        setInvoiceError(data.error || 'Invoice No generate nahi ho saka');
      }
    } catch (err) {
      console.error('Error generating invoice no:', err);
      setFormData(prev => ({ ...prev, invoice_no: '' }));
      setInvoiceError('Server se connect nahi ho saka');
    } finally {
      setInvoiceLoading(false);
    }
  };

  const fetchDeliveryOrderDetails = async (deliveryOrderId) => {
    if (!deliveryOrderId) {
      setFilteredCmas([]);
      setFilteredChas([]);
      setSelectedDeliveryOrder(null);
      setDeliveryOrderQuantity(null);
      setQuantityError('');
      setInvoiceError('');
      setFormData(prev => ({ 
        ...prev, 
        cha_cma_id: '', 
        warehouse_id: '', 
        broker_id: '', 
        sale_price_per_mt: '', 
        total_amount: '',
        invoice_no: ''
      }));
      return;
    }
    
    try {
      const deliveryOrder = deliveryOrders.find(order => order.do_id === deliveryOrderId);
      
      if (deliveryOrder) {
        setSelectedDeliveryOrder(deliveryOrder);
        setDeliveryOrderQuantity(deliveryOrder);
        
        if (deliveryOrder.cha_cma_id) {
          if (deliveryOrder.check_cma_cha === 'CMA') {
            const matchingCma = cmas.find(cma => cma.cha_cma_id === deliveryOrder.cha_cma_id);
            if (matchingCma) {
              setFilteredCmas([matchingCma]);
              setFilteredChas([]);
              setSelectedType('CMA');
              setFormData(prev => ({ 
                ...prev, 
                cha_cma_id: deliveryOrder.cha_cma_id,
                check_cma_cha: 'CMA',
                warehouse_id: deliveryOrder.warehouse_id || ''
              }));
              fetchWarehousesByCma(deliveryOrder.cha_cma_id);
            }
          } else if (deliveryOrder.check_cma_cha === 'CHA') {
            const matchingCha = chas.find(cha => cha.cha_cma_id === deliveryOrder.cha_cma_id);
            if (matchingCha) {
              setFilteredChas([matchingCha]);
              setFilteredCmas([]);
              setSelectedType('CHA');
              setFormData(prev => ({ 
                ...prev, 
                cha_cma_id: deliveryOrder.cha_cma_id,
                check_cma_cha: 'CHA',
                warehouse_id: deliveryOrder.warehouse_id || ''
              }));
              fetchWarehousesByCha(deliveryOrder.cha_cma_id);
            }
          }
        }

        const availableQty = (parseFloat(deliveryOrder.quantity_mt || 0) - parseFloat(deliveryOrder.used_quantity_mt || 0)) || '';
        const salePrice = deliveryOrder.sale_price_per_mt || '';
        const totalAmount = availableQty && salePrice ? (parseFloat(availableQty) * parseFloat(salePrice)).toFixed(2) : '';
        
        setFormData(prev => ({
          ...prev,
          commodity_id: deliveryOrder.commodity_id || '',
          quantity_mt: availableQty,
          domestic_buyer_id: deliveryOrder.buyer_id || '',
          broker_id: deliveryOrder.broker_id || '',
          sale_price_per_mt: salePrice,
          total_amount: totalAmount,
        }));

        // ← AUTO GENERATE INVOICE NO (only if order_date is already filled)
        await fetchGeneratedInvoiceNo(deliveryOrderId, formData.order_date);
      }
    } catch (error) {
      console.error('Error fetching delivery order details:', error);
      setFilteredCmas([]);
      setFilteredChas([]);
      setSelectedDeliveryOrder(null);
      setDeliveryOrderQuantity(null);
    }
  };

  const fetchWarehousesByCma = async (cmaId) => {
    if (!cmaId) {
      setWarehouses([]);
      return;
    }
    try {
      const warehouseRes = await fetch(`${API_BASE_URL}/api/masters/dropdown-warehouse-list-by-cma?cha_cma_id=${cmaId}`, {
        credentials: 'include'
      });
      if (warehouseRes.ok) {
        const warehouseData = await warehouseRes.json();
        setWarehouses(warehouseData.data || []);
        setChaWarehouses([]);
      }
    } catch (error) {
      console.error('Error fetching warehouses:', error);
      setWarehouses([]);
    }
  };

  const fetchWarehousesByCha = async (chaId) => {
    if (!chaId) {
      setChaWarehouses([]);
      return;
    }
    try {
      const warehouseRes = await fetch(`${API_BASE_URL}/api/masters/dropdown-warehouse-list-by-cha?cha_cma_id=${chaId}`, {
        credentials: 'include'
      });
      if (warehouseRes.ok) {
        const warehouseData = await warehouseRes.json();
        setChaWarehouses(warehouseData.data || []);
        setWarehouses([]);
      }
    } catch (error) {
      console.error('Error fetching CHA warehouses:', error);
      setChaWarehouses([]);
    }
  };

  const handleTypeChange = (type) => {
    setSelectedType(type);
    setFormData(prev => ({ 
      ...prev, 
      cha_cma_id: '', 
      check_cma_cha: type,
      warehouse_id: '' 
    }));
    setWarehouses([]);
    setChaWarehouses([]);
    
    if (!selectedDeliveryOrder) {
      setFilteredCmas([]);
      setFilteredChas([]);
    }
  };

  const getMaxDate = () => new Date().toISOString().split('T')[0]; // YYYY-MM-DD

  const preventDateTyping = (e) => {
    if (e.key !== 'Tab' && e.key !== 'Escape') e.preventDefault();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === 'order_date' && value) {
      const maxDate = getMaxDate();
      if (value > maxDate) {
        setMessage({ type: 'error', text: 'Future dates are not allowed for Order Date.' });
        return;
      }
    }
    if (name === 'order_date') {
      setMessage(prev => (prev?.type === 'error' && prev?.text === 'Future dates are not allowed for Order Date.' ? null : prev));
    }
    
    let updatedFormData = { ...formData, [name]: value };
    
    if (name === 'quantity_mt' || name === 'sale_price_per_mt') {
      const quantity = name === 'quantity_mt' ? parseFloat(value) || 0 : parseFloat(formData.quantity_mt) || 0;
      const salePrice = name === 'sale_price_per_mt' ? parseFloat(value) || 0 : parseFloat(formData.sale_price_per_mt) || 0;
      
      if (quantity > 0 && salePrice > 0) {
        updatedFormData.total_amount = (quantity * salePrice).toFixed(2);
      } else if (quantity === 0 || salePrice === 0) {
        updatedFormData.total_amount = '';
      }
    }
    
    setFormData(updatedFormData);
    
    if (name === 'quantity_mt') {
      validateQuantity(value, deliveryOrderQuantity);
    }

    // ← Jab Order Date change ho aur Delivery Order pehle se select hai → Invoice regenerate karo
    if (name === 'order_date' && value && formData.delivery_order_id) {
      fetchGeneratedInvoiceNo(formData.delivery_order_id, value);
    }
    
    if (message && message.type === 'error' && message.text.includes('Quantity')) {
      setMessage(null);
    }
  };

  const handleDropdownChange = (fieldName, value) => {
    setFormData(prev => ({ ...prev, [fieldName]: value }));
    
    if (fieldName === 'delivery_order_id') {
      fetchDeliveryOrderDetails(value);
    }
    
    if (fieldName === 'cha_cma_id') {
      if (selectedType === 'CMA') {
        fetchWarehousesByCma(value);
      } else {
        fetchWarehousesByCha(value);
      }
      setFormData(prev => ({ ...prev, warehouse_id: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (quantityError) {
      setMessage({ type: 'error', text: 'Please fix the quantity error before submitting' });
      return;
    }

    if (selectedDeliveryOrder && (parseFloat(selectedDeliveryOrder.quantity_mt || 0) - parseFloat(selectedDeliveryOrder.used_quantity_mt || 0)) <= 0) {
      setMessage({ 
        type: 'error', 
        text: 'No quantity available for this delivery order. All quantity has been used in previous sales orders.' 
      });
      return;
    }

    const maxDate = getMaxDate();
    if (formData.order_date && formData.order_date > maxDate) {
      setMessage({ type: 'error', text: 'Future dates are not allowed for Order Date.' });
      return;
    }

    setLoading(true);
    setMessage(null);

    const processedFormData = { ...formData };
    ['order_date'].forEach(field => {
      if (processedFormData[field] === '') {
        processedFormData[field] = null;
      }
    });

    if (processedFormData.broker_id === '') {
      processedFormData.broker_id = null;
    }

    try {
      const res = await fetch(`${API_BASE_URL}/api/sales/sales-order-post`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(processedFormData),
      });
      
      const data = await res.json();
      if (res.ok) {
        setMessage({ type: 'success', text: 'Sales Order Created Successfully!' });
        
        // ← REFETCH DELIVERY ORDERS TO UPDATE QUANTITIES IMMEDIATELY
        await fetchDeliveryOrders();
        
        setFormData({
          order_date: '',
          warehouse_id: '',
          broker_id: '',
          domestic_buyer_id: '',
          commodity_id: '',
          quantity_mt: '',
          sale_price_per_mt: '',
          total_amount: '',
          remarks: '',
          cha_cma_id: '',
          check_cma_cha: 'CMA',
          penalty_interest_pct: '',
          delivery_order_id: '',
          invoice_no: '',
          vehicle_no: '',
          issuing_authority_id: ''
        });
        setFilteredCmas([]);
        setFilteredChas([]);
        setWarehouses([]);
        setChaWarehouses([]);
        setSelectedDeliveryOrder(null);
        setDeliveryOrderQuantity(null);
        setQuantityError('');
        setSelectedType('CMA');
      } else {
        if (data.duplicate_invoice) {
          // ← Duplicate invoice — show clear error + auto-regenerate next number
          setMessage({ 
            type: 'error', 
            text: `⚠️ ${data.error} System is generating the next available Invoice No...` 
          });
          // Auto-regenerate so next available number fills in
          await fetchGeneratedInvoiceNo(formData.delivery_order_id, formData.order_date);
        } else {
          setMessage({ type: 'error', text: data.error || 'Something went wrong' });
        }
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Server Error' });
    }
    setLoading(false);
  };

  return (
    <div className="max-w-8xl mx-auto p-0 bg-white">
      <div className="flex justify-between items-center border-b border-gray-100 bg-gray-50 px-4 py-1.5">
        <h2 className="text-lg font-bold mb-0 text-left">Sales Order Entry</h2>
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="flex items-center gap-1 text-[#10a5e9] border border-[#10a5e9] px-2 py-0.5 rounded text-xs font-medium hover:bg-[#eaf6fd]"
        >
          <FaArrowLeft />
          Back
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-0">
        <div className="px-4 pt-4 pb-2">
          {/* Delivery Order and Order Date at the top */}
          <div className="mb-3 grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium mb-1">Delivery Order No</label>
              <SearchableDropdown
                options={deliveryOrders}
                value={formData.delivery_order_id}
                onChange={(value) => handleDropdownChange('delivery_order_id', value)}
                placeholder="Select Delivery Order"
                displayKey="do_number"
                valueKey="do_id"
                required
                customDisplay={(item) => `${item.do_number} - ${item.buyer_name} (Available: ${(parseFloat(item.quantity_mt || 0) - parseFloat(item.used_quantity_mt || 0)).toLocaleString('en-IN', {
                  minimumFractionDigits: 3,
                  maximumFractionDigits: 3
                })} MT)`}
              />
              {selectedDeliveryOrder && (
                <div className="mt-2 p-2 bg-gray-50 rounded-sm border border-gray-200">
                  <div className="text-xs text-gray-700 space-y-1">
                    <div className="flex justify-between">
                      <span>Total DO Quantity:</span>
                      <span className="font-medium text-blue-600">
                        {parseFloat(selectedDeliveryOrder.quantity_mt || 0).toLocaleString('en-IN', {
                          minimumFractionDigits: 3,
                          maximumFractionDigits: 3
                        })} MT
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>Used in Sales Orders:</span>
                      <span className="font-medium text-orange-600">
                        {parseFloat(selectedDeliveryOrder.used_quantity_mt || 0).toLocaleString('en-IN', {
                          minimumFractionDigits: 3,
                          maximumFractionDigits: 3
                        })} MT
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>Available Quantity:</span>
                      <span className={`font-medium ${
                        (parseFloat(selectedDeliveryOrder.quantity_mt || 0) - parseFloat(selectedDeliveryOrder.used_quantity_mt || 0)) <= 0 
                          ? 'text-red-600' 
                          : 'text-green-600'
                      }`}>
                        {(parseFloat(selectedDeliveryOrder.quantity_mt || 0) - parseFloat(selectedDeliveryOrder.used_quantity_mt || 0)).toLocaleString('en-IN', {
                          minimumFractionDigits: 3,
                          maximumFractionDigits: 3
                        })} MT
                      </span>
                    </div>
                    {(parseFloat(selectedDeliveryOrder.quantity_mt || 0) - parseFloat(selectedDeliveryOrder.used_quantity_mt || 0)) <= 0 && (
                      <div className="text-red-600 font-medium text-xs mt-1">
                        ⚠️ No quantity available for this delivery order
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">Order Date</label>
              <input 
                type="date" 
                name="order_date" 
                value={formData.order_date} 
                onChange={handleChange} 
                onKeyDown={preventDateTyping}
                max={getMaxDate()}
                className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-[#10a5e9] rounded-none cursor-pointer" 
                title="Select date from calendar only (future dates not allowed)"
                required 
              />
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {/* CMA/CHA Checkbox Section */}
            <div className="md:col-span-2 flex gap-6 items-center mb-2">
              <label className="flex items-center gap-1">
                <input 
                  type="checkbox" 
                  checked={selectedType === 'CMA'} 
                  onChange={() => handleTypeChange('CMA')}
                  disabled={!!selectedDeliveryOrder}
                /> 
                CMA
              </label>
              <label className="flex items-center gap-1">
                <input 
                  type="checkbox" 
                  checked={selectedType === 'CHA'} 
                  onChange={() => handleTypeChange('CHA')}
                  disabled={!!selectedDeliveryOrder}
                /> 
                CHA
              </label>
              {selectedDeliveryOrder && (
                <span className="text-xs text-gray-500 italic">
                  (Auto-selected from Delivery Order)
                </span>
              )}
            </div>

            {/* Conditional CMA Dropdown */}
            {selectedType === 'CMA' && (
              <div>
                <label className="block text-xs font-medium mb-1">CMA</label>
                <SearchableDropdown
                  options={filteredCmas.length > 0 ? filteredCmas : cmas}
                  value={formData.cha_cma_id}
                  onChange={(value) => handleDropdownChange('cha_cma_id', value)}
                  placeholder={selectedDeliveryOrder ? "CMA auto-selected from delivery order" : "Select CMA"}
                  displayKey="name"
                  valueKey="cha_cma_id"
                  required
                  disabled={!!selectedDeliveryOrder}
                />
              </div>
            )}

            {/* Conditional CHA Dropdown */}
            {selectedType === 'CHA' && (
              <div>
                <label className="block text-xs font-medium mb-1">CHA</label>
                <SearchableDropdown
                  options={filteredChas.length > 0 ? filteredChas : chas}
                  value={formData.cha_cma_id}
                  onChange={(value) => handleDropdownChange('cha_cma_id', value)}
                  placeholder={selectedDeliveryOrder ? "CHA auto-selected from delivery order" : "Select CHA"}
                  displayKey="name"
                  valueKey="cha_cma_id"
                  required
                  disabled={!!selectedDeliveryOrder}
                />
              </div>
            )}

            {/* Conditional Warehouse Dropdown */}
            {selectedType === 'CMA' && (
              <div>
                <label className="block text-xs font-medium mb-1">Warehouse</label>
                <SearchableDropdown
                  options={warehouses.map(w => ({ ...w, name: `${w.name} (${w.location})` }))}
                  value={formData.warehouse_id}
                  onChange={(value) => handleDropdownChange('warehouse_id', value)}
                  placeholder="Select Warehouse"
                  displayKey="name"
                  valueKey="warehouse_id"
                  required
                  disabled={!!selectedDeliveryOrder}
                />
              </div>
            )}

            {selectedType === 'CHA' && (
              <div>
                <label className="block text-xs font-medium mb-1">Warehouse/Port</label>
                <SearchableDropdown
                  options={chaWarehouses.map(w => ({ 
                    ...w, 
                    name: `${w.name || w.warehouse_name || w.port_name} ${w.location ? `(${w.location})` : ''}` 
                  }))}
                  value={formData.warehouse_id}
                  onChange={(value) => handleDropdownChange('warehouse_id', value)}
                  placeholder="Select Warehouse/Port"
                  displayKey="name"
                  valueKey="warehouse_id"
                  required
                  disabled={!!selectedDeliveryOrder}
                />
              </div>
            )}

            <div>
              <label className="block text-xs font-medium mb-1">Broker</label>
              <SearchableDropdown
                options={brokers}
                value={formData.broker_id}
                onChange={(value) => handleDropdownChange('broker_id', value)}
                placeholder={selectedDeliveryOrder ? "Auto-selected from Delivery Order" : "Select Broker"}
                displayKey="name"
                valueKey="broker_id"
                disabled={!!selectedDeliveryOrder}
              />
            </div>

            <div>
              <label className="block text-xs font-medium mb-1">Domestic Buyer</label>
              <SearchableDropdown
                options={domesticBuyers}
                value={formData.domestic_buyer_id}
                onChange={(value) => handleDropdownChange('domestic_buyer_id', value)}
                placeholder="Select Domestic Buyer"
                displayKey="name"
                valueKey="domestic_buyer_id"
                required
                disabled={!!selectedDeliveryOrder}
                customDisplay={(item) => `${item.name}${item.state ? `, ${item.state}` : ''}`}
              />
            </div>

            <div>
              <label className="block text-xs font-medium mb-1">Commodity</label>
              <SearchableDropdown
                options={commodities}
                value={formData.commodity_id}
                onChange={(value) => handleDropdownChange('commodity_id', value)}
                placeholder="Select Commodity"
                displayKey="name"
                valueKey="commodity_id"
                required
                customDisplay={(item) => `${item.name} (${item.origin || 'N/A'})`}
                disabled={!!selectedDeliveryOrder}
              />
            </div>

            <div>
              <label className="block text-xs font-medium mb-1">
                Quantity (MT)
                {selectedDeliveryOrder && (
                  <span className="text-gray-500 ml-1">
                    (Max: {(parseFloat(selectedDeliveryOrder.quantity_mt || 0) - parseFloat(selectedDeliveryOrder.used_quantity_mt || 0)).toLocaleString('en-IN', {
                      minimumFractionDigits: 3,
                      maximumFractionDigits: 3
                    })} MT)
                  </span>
                )}
              </label>
              <input 
                type="number" 
                step="0.001"
                name="quantity_mt" 
                value={formData.quantity_mt} 
                onChange={handleChange} 
                className={`w-full border px-3 py-1.5 text-sm bg-white focus:outline-none rounded-none ${
                  quantityError ? 'border-red-500 focus:border-red-500' : 'border-gray-300 focus:border-[#10a5e9]'
                }`}
                max={selectedDeliveryOrder ? (parseFloat(selectedDeliveryOrder.quantity_mt || 0) - parseFloat(selectedDeliveryOrder.used_quantity_mt || 0)) : undefined}
                required 
              />
              {quantityError && (
                <div className="text-red-500 text-xs mt-1">{quantityError}</div>
              )}
            </div>

            <div>
              <label className="block text-xs font-medium mb-1">Sale Price/MT</label>
              <input 
                type="number" 
                step="0.01"
                name="sale_price_per_mt" 
                value={formData.sale_price_per_mt} 
                onChange={handleChange} 
                className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-[#10a5e9] rounded-none" 
                required 
                disabled={!!selectedDeliveryOrder}
                readOnly={!!selectedDeliveryOrder}
              />
            </div>

            <div>
              <label className="block text-xs font-medium mb-1">Total_Amount</label>
              <input 
                type="number" 
                step="0.01"
                name="total_amount" 
                value={formData.total_amount} 
                onChange={handleChange} 
                className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-gray-100 focus:outline-none rounded-none cursor-not-allowed" 
                required 
                readOnly={true}
              />
            </div>

            <div>
              <label className="block text-xs font-medium mb-1">Penalty Interest (%)</label>
              <input 
                type="number" 
                step="0.01"
                name="penalty_interest_pct" 
                value={formData.penalty_interest_pct} 
                onChange={handleChange} 
                className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-[#10a5e9] rounded-none" 
              />
            </div>

            <div>
              <label className="block text-xs font-medium mb-1">
                Invoice No
                {formData.delivery_order_id && (
                  <span className="text-gray-400 text-xs ml-1 font-normal">(Auto-generated)</span>
                )}
              </label>
              <div className="flex gap-1 items-center">
                <input 
                  type="text" 
                  name="invoice_no" 
                  value={invoiceLoading ? '' : formData.invoice_no} 
                  onChange={handleChange}
                  readOnly={!!formData.delivery_order_id}
                  className={`w-full border px-3 py-1.5 text-sm focus:outline-none rounded-none ${
                    formData.delivery_order_id
                      ? 'bg-gray-100 border-gray-300 cursor-not-allowed text-gray-700 font-medium'
                      : 'bg-white border-gray-300 focus:border-[#10a5e9]'
                  }`}
                  placeholder={
                    invoiceLoading
                      ? 'Generating...'
                      : formData.delivery_order_id && !formData.invoice_no
                      ? 'Could not generate - check Sales Contract'
                      : 'Enter Invoice No'
                  }
                  required
                />
                {formData.delivery_order_id && (
                  <button
                    type="button"
                    onClick={() => fetchGeneratedInvoiceNo(formData.delivery_order_id, formData.order_date)}
                    disabled={invoiceLoading}
                    title="Regenerate Invoice No"
                    className="border border-gray-300 px-2 py-1.5 text-xs bg-white hover:bg-gray-50 text-gray-600 rounded-none flex-shrink-0 disabled:opacity-50"
                  >
                    {invoiceLoading ? '...' : '↻'}
                  </button>
                )}
              </div>
              {invoiceLoading && (
                <div className="text-xs text-blue-500 mt-0.5">⏳ Generating invoice number...</div>
              )}
              {!invoiceLoading && formData.invoice_no && formData.delivery_order_id && (
                <div className="text-xs text-green-600 mt-0.5">✓ {formData.invoice_no}</div>
              )}
              {!invoiceLoading && invoiceError && (
                <div className="text-xs text-red-500 mt-0.5">⚠️ {invoiceError}</div>
              )}
            </div>

            <div>
              <label className="block text-xs font-medium mb-1">Vehicle No</label>
              <input 
                type="text" 
                name="vehicle_no" 
                value={formData.vehicle_no} 
                onChange={handleChange} 
                className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-[#10a5e9] rounded-none" 
              />
            </div>

            <div>
            <label className="block text-xs font-medium mb-1">Issuing Authority</label>
            <SearchableDropdown
              options={issuingAuthorities}
              value={formData.issuing_authority_id}
              onChange={(value) => handleDropdownChange('issuing_authority_id', value)}
              placeholder="Select Issuing Authority"
              displayKey="authority_name"
              valueKey="id"
              required
            />
          </div>

          </div>

          <div className="mt-3">
            <label className="block text-xs font-medium mb-1">Remarks</label>
            <textarea 
              name="remarks" 
              value={formData.remarks} 
              onChange={handleChange} 
              className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-[#10a5e9] rounded-none resize-none" 
              rows={3}
            ></textarea>
          </div>
        </div>

        <div className="px-4 pt-2 pb-4">
          <button
            type="submit"
            className="w-auto px-4 py-1.5 text-sm bg-[#3899cf] text-white font-semibold tracking-wide rounded border border-[#10a5e9] hover:bg-[#0e8ac2] transition disabled:opacity-60"
            disabled={loading || 
              !!quantityError ||
              (selectedDeliveryOrder && (parseFloat(selectedDeliveryOrder.quantity_mt || 0) - parseFloat(selectedDeliveryOrder.used_quantity_mt || 0)) <= 0)
            }
          >
            {loading ? 'Saving...' : 'Save Sales Order'}
          </button>
          {message && (
            <div className={`mt-2 text-center text-sm ${message.type === 'success' ? 'text-green-600' : 'text-red-600'}`}>
              {message.text}
            </div>
          )}
        </div>
      </form>
    </div>
  );
};

export default SalesOrder;
